import { useState } from 'react';
import type { MultimodalAssetDto } from '@scale/mm-shared';
import { Button, Modal, Spinner } from '@scale/scaleui';
import ImageMetadataTags from './ImageMetadataTags';

interface FocusedImageModalProps {
  image: Partial<MultimodalAssetDto> & { image_url: string };
  onCancel: () => void;
  onConfirm?: () => void;
  flagLowQuality?: (image_url: string) => Promise<void>;
}

const FocusedImageModal = ({
  image,
  onCancel,
  onConfirm,
  flagLowQuality,
}: FocusedImageModalProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailedLoading, setImageFailedLoading] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageFailedLoading(false);
  };

  const handleImageError = () => {
    setImageLoaded(false);
    setImageFailedLoading(true);
  };

  return (
    <Modal
      open
      hideTitle
      className="max-w-full max-h-full overflow-auto scale scale-tailwind"
      innerClassName="p-4"
      onClose={onCancel}
      actions={
        <div className="flex w-full gap-2">
          {flagLowQuality && imageLoaded && !imageFailedLoading && (
            <Button
              variant="danger"
              onClick={async () => {
                onCancel();
                await flagLowQuality(image.image_url);
              }}
            >
              Flag Low Quality
            </Button>
          )}
          <Button variant="secondary" onClick={onCancel} className="ml-auto">
            Go back
          </Button>
          {onConfirm && imageLoaded && !imageFailedLoading && (
            <Button variant="primary" onClick={onConfirm} data-testid="confirm-image-button">
              Add
            </Button>
          )}
        </div>
      }
    >
      <div>
        <img
          src={image.image_url}
          alt={image.image_category?.join(' ')}
          className="ml-auto mr-auto max-w-[80vw] max-h-[70vh]"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ display: imageLoaded ? 'block' : 'none' }}
        />
        {!imageLoaded && !imageFailedLoading && <Spinner size="lg" />}
        {imageFailedLoading && <div>Failed to load image</div>}
        <ImageMetadataTags image={image}></ImageMetadataTags>
      </div>
    </Modal>
  );
};

export default FocusedImageModal;
